import React, { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useData } from '../context/DataContext';
import { usePerfil } from '../context/PerfilContext';
import { usePlus } from '../context/PlusContext';
import { LIMITES_USO } from '../config/planes';
import { TrendingUp, TrendingDown, Minus, ChevronLeft, ChevronRight, ChevronDown, FileDown } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { exportarInformesPDF } from '../utils/pdfUtils';
import { exportarInformeCompletoPDF } from '../utils/pdfInformeCompleto';
import { computa } from '../utils/transaccionUtils';
import { limiteEnPeriodo, limiteMensualEnAnio } from '../utils/presupuestoUtils';
import { formatoEuros } from '../utils/numeros';
import {
  limiteMensualCategoria, mediaMesesAnteriores, mesesParaMedia, serieCategoria,
} from '../utils/informes';
import { EtiquetaPlus, TooltipEuros, ejeEuros, useColoresGrafico } from '../components/informes/comun';
import { SeccionResumen } from '../components/informes/SeccionResumen';
import { SeccionPresupuestos } from '../components/informes/SeccionPresupuestos';
import { SeccionCuentas } from '../components/informes/SeccionCuentas';
import { SeccionHabitos } from '../components/informes/SeccionHabitos';
import { SeccionAdelantos } from '../components/informes/SeccionAdelantos';

// ── Helpers ───────────────────────────────────────────────────────────────────

function mesAnterior(yyyy: number, mm: number): [number, number] {
  return mm === 1 ? [yyyy - 1, 12] : [yyyy, mm - 1];
}

function labelPeriodo(yyyy: number, mm: number) {
  return new Date(yyyy, mm - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
}

function diasEnMes(anio: number, mes: number): number {
  return new Date(anio, mes, 0).getDate();
}

function sumCat(
  transacciones: ReturnType<typeof useData>['transacciones'],
  categoriaId: string,
  tipo: 'ingreso' | 'gasto',
  yyyy: number,
  mm: number | null,
): number {
  return transacciones
    .filter(t => {
      if (t.tipo !== tipo || !computa(t)) return false;
      const f = new Date(t.fecha);
      if (f.getFullYear() !== yyyy) return false;
      if (mm !== null && f.getMonth() + 1 !== mm) return false;
      return t.categoriaId === categoriaId;
    })
    .reduce((s, t) => s + t.cantidad, 0);
}

function sumTotal(
  transacciones: ReturnType<typeof useData>['transacciones'],
  tipo: 'ingreso' | 'gasto',
  yyyy: number,
  mm: number | null,
): number {
  return transacciones
    .filter(t => {
      if (t.tipo !== tipo || !computa(t)) return false;
      const f = new Date(t.fecha);
      if (f.getFullYear() !== yyyy) return false;
      if (mm !== null && f.getMonth() + 1 !== mm) return false;
      return true;
    })
    .reduce((s, t) => s + t.cantidad, 0);
}

// ¿El presupuesto p aplica al periodo mostrado, sea cual sea su periodicidad?
// Una categoría puede tener a la vez presupuestos mensuales y anuales, y el
// desglose debe mostrarlos todos:
//  · Vista mensual → mensuales del mes exacto + anuales de ese año.
//  · Vista anual   → anuales del año + mensuales de cualquier mes de ese año.
function presupuestoEnPeriodo(
  p: { periodicidad: 'mensual' | 'anual'; mes: string; repetitivo: boolean },
  anio: number,
  mes: number | null, // mes 1–12 en vista mensual; null en vista anual
): boolean {
  // Modelo evergreen: un presupuesto REPETITIVO aplica desde su inicio (p.mes) en
  // adelante; uno PUNTUAL solo en su período exacto.
  if (p.periodicidad === 'anual') {
    return p.repetitivo ? p.mes <= String(anio) : p.mes === String(anio);
  }
  // mensual
  if (mes !== null) {
    const periodo = `${anio}-${String(mes).padStart(2, '0')}`;
    return p.repetitivo ? p.mes <= periodo : p.mes === periodo;
  }
  // vista anual mostrando mensuales de cualquier mes de ese año
  return p.repetitivo ? p.mes.slice(0, 4) <= String(anio) : p.mes.startsWith(`${anio}-`);
}

// ── C4: evolución de una categoría ───────────────────────────────────────────

const EvolucionCategoria: React.FC<{
  categoriaId: string; tipo: 'gasto' | 'ingreso'; anioFin: number; mesFin: number;
}> = ({ categoriaId, tipo, anioFin, mesFin }) => {
  const { transacciones } = useData();
  const c = useColoresGrafico();
  const serie = useMemo(
    () => serieCategoria(transacciones, categoriaId, tipo, anioFin, mesFin, 12),
    [transacciones, categoriaId, tipo, anioFin, mesFin],
  );
  const conDatos = serie.filter(p => p.total > 0);
  const media = conDatos.length ? conDatos.reduce((s, p) => s + p.total, 0) / conDatos.length : 0;
  const color = tipo === 'gasto' ? c.gastos : c.ingresos;
  return (
    <div className="px-4 md:px-5 py-3 border-t border-gray-100 dark:border-gray-700">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
        Últimos 12 meses · media <span className="font-semibold tabular-nums">{formatoEuros(media)}</span> en los meses con movimientos
      </p>
      <div className="h-32">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={serie} margin={{ top: 6, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={c.rejilla} />
            <XAxis dataKey="etiqueta" tick={{ fontSize: 10, fill: c.suave }} tickLine={false} axisLine={{ stroke: c.rejilla }} interval={1} />
            <YAxis tick={{ fontSize: 10, fill: c.suave }} tickLine={false} axisLine={false} tickFormatter={ejeEuros} width={52} />
            <Tooltip cursor={{ fill: c.rejilla, opacity: 0.5 }} content={<TooltipEuros titulo={l => {
              const p = serie.find(x => x.etiqueta === l);
              return p ? new Date(p.anio, p.mes - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }) : l;
            }} />} />
            {media > 0 && <ReferenceLine y={media} stroke={c.tinta} strokeDasharray="4 3" />}
            <Bar dataKey="total" name={tipo === 'gasto' ? 'Gastado' : 'Ingresado'} fill={color} radius={[4, 4, 0, 0]} maxBarSize={16} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ── Componente ────────────────────────────────────────────────────────────────

type Vista   = 'mensual' | 'anual';
type TipoTx  = 'gasto' | 'ingreso';
/** Con qué se compara cada categoría en la vista mensual (I8: los dos últimos son de Plus). */
type Comparador = 'mesAnterior' | 'anioPasado' | 'media6';

const SECCIONES = [
  { id: 'resumen', nombre: 'Resumen' },
  { id: 'categorias', nombre: 'Categorías' },
  { id: 'presupuestos', nombre: 'Presupuestos' },
  { id: 'cuentas', nombre: 'Cuentas' },
  { id: 'habitos', nombre: 'Hábitos' },
  { id: 'adelantos', nombre: 'Adelantos' },
] as const;
type Seccion = typeof SECCIONES[number]['id'];

export const Informes: React.FC = () => {
  const datosFinanzas = useData();
  const { transacciones, categorias, cuentas, presupuestos } = datosFinanzas;

  // Sección elegida, en la URL (?seccion=resumen) para que la notificación del resumen abra la buena.
  const [params, setParams] = useSearchParams();
  const seccion: Seccion = SECCIONES.some(x => x.id === params.get('seccion'))
    ? params.get('seccion') as Seccion : 'resumen';
  const irSeccion = (id: Seccion) => setParams(id === 'resumen' ? {} : { seccion: id }, { replace: true });
  const [comparador, setComparador] = useState<Comparador>('mesAnterior');

  const hoy = new Date();
  const [vista, setVista]     = useState<Vista>('mensual');
  const [tipoTx, setTipoTx]   = useState<TipoTx>('gasto');
  const [mesActual, setMesActual]   = useState(hoy.getMonth() + 1);
  const [anioActual, setAnioActual] = useState(hoy.getFullYear());
  const [anioSel, setAnioSel]       = useState(hoy.getFullYear());

  // Categoría expandida para ver desglose de presupuestos
  const [expandedCatId, setExpandedCatId] = useState<string | null>(null);
  // Clave de presupuesto expandido: "${catId}::${presupuestoId}" o "${catId}::sin"
  const [expandedBudgetKey, setExpandedBudgetKey] = useState<string | null>(null);

  // Plan gratis: solo los últimos LIMITES_USO.mesesInformes meses (el actual incluido).
  // Ir más atrás abre la hoja de Plus.
  const { esPlus } = usePerfil();
  const { ofrecerPlus } = usePlus();
  const elegirComparador = (c: Comparador) => {
    if (c !== 'mesAnterior' && !esPlus) { ofrecerPlus('informesPlus'); return; }
    setComparador(c);
  };
  const masAntiguo = new Date(hoy.getFullYear(), hoy.getMonth() - (LIMITES_USO.mesesInformes - 1), 1);

  const irMesAnterior = () => {
    const [y, m] = mesAnterior(anioActual, mesActual);
    if (!esPlus && new Date(y, m - 1, 1) < masAntiguo) { ofrecerPlus('informes'); return; }
    setAnioActual(y); setMesActual(m);
  };
  const irAnioAnterior = () => {
    if (!esPlus && anioSel - 1 < masAntiguo.getFullYear()) { ofrecerPlus('informes'); return; }
    setAnioSel(y => y - 1);
  };
  const irMesSiguiente = () => {
    const m2 = mesActual === 12 ? 1 : mesActual + 1;
    const y2 = mesActual === 12 ? anioActual + 1 : anioActual;
    setAnioActual(y2); setMesActual(m2);
  };

  const [anioAnt, mesAnt] = vista === 'mensual'
    ? mesAnterior(anioActual, mesActual)
    : [anioSel - 1, null];

  // Con qué se compara: en la vista anual, siempre el año anterior.
  const comparar: Comparador = vista === 'mensual' ? comparador : 'mesAnterior';

  const periodoActualLabel = vista === 'mensual'
    ? labelPeriodo(anioActual, mesActual)
    : String(anioSel);
  const periodoAntLabel = vista === 'anual'
    ? String(anioSel - 1)
    : comparar === 'anioPasado'
      ? labelPeriodo(anioActual - 1, mesActual)
      : comparar === 'media6'
        ? 'media 6 meses'
        : labelPeriodo(anioAnt, mesAnt as number);

  // Lo de una categoría en el periodo con el que se compara.
  const referenciaCat = useCallback((catId: string) => {
    if (vista === 'anual') return sumCat(transacciones, catId, tipoTx, anioSel - 1, null);
    if (comparar === 'anioPasado') return sumCat(transacciones, catId, tipoTx, anioActual - 1, mesActual);
    if (comparar === 'media6') return mediaMesesAnteriores(transacciones, tipoTx, anioActual, mesActual, 6, catId);
    return sumCat(transacciones, catId, tipoTx, anioAnt, mesAnt as number);
  }, [transacciones, tipoTx, vista, comparar, anioSel, anioActual, mesActual, anioAnt, mesAnt]);

  const vistaInforme = { anio: vista === 'mensual' ? anioActual : anioSel, mes: vista === 'mensual' ? mesActual : null };

  // C2: la media mensual del año en curso se reparte entre los meses que llevamos.
  const mesesMedia = vista === 'anual' ? mesesParaMedia(anioSel) : 12;

  // ── Progreso mensual (solo mes actual) ──────────────────────────────────────
  const esMesActual =
    vista === 'mensual' &&
    mesActual === hoy.getMonth() + 1 &&
    anioActual === hoy.getFullYear();

  const progreso = useMemo(() => {
    if (!esMesActual) return null;
    const diaHoy = hoy.getDate();
    const totalDiasMes = diasEnMes(hoy.getFullYear(), hoy.getMonth() + 1);
    const [aAnt, mAnt] = mesAnterior(hoy.getFullYear(), hoy.getMonth() + 1);
    const gastoActual = sumTotal(transacciones, tipoTx, hoy.getFullYear(), hoy.getMonth() + 1);
    const gastoPrevMes = sumTotal(transacciones, tipoTx, aAnt, mAnt);
    const ritmoEsperado = gastoPrevMes > 0 ? (gastoPrevMes / totalDiasMes) * diaHoy : 0;
    const porcentajeBarra = gastoPrevMes > 0 ? Math.min((gastoActual / gastoPrevMes) * 100, 100) : 0;
    const porcentajeRitmo = Math.min((diaHoy / totalDiasMes) * 100, 100);
    const diferencia = gastoActual - ritmoEsperado;
    return {
      gastoActual, gastoPrevMes, ritmoEsperado,
      porcentajeBarra, porcentajeRitmo,
      diferencia, adelantado: diferencia > 0.01, enRitmo: Math.abs(diferencia) < 0.01,
      diaHoy, totalDiasMes,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esMesActual, transacciones, tipoTx]);

  // ── Datos por categoría (incluye métricas de ritmo para el mes actual) ────────
  const datos = useMemo(() => {
    // Parámetros de ritmo: solo se usan cuando esMesActual, pero los calculamos
    // siempre para que el tipo sea uniforme y no haya condicionales en el render.
    const diaHoy             = hoy.getDate();
    const totalDiasMesActual = diasEnMes(hoy.getFullYear(), hoy.getMonth() + 1);
    const [yAntR, mAntR]     = mesAnterior(hoy.getFullYear(), hoy.getMonth() + 1);
    const totalDiasMesAnt    = diasEnMes(yAntR, mAntR);
    const diasRestantes      = totalDiasMesActual - diaHoy;

    return categorias
      .filter(c => c.tipo === tipoTx)
      .map(cat => {
        const actual = sumCat(transacciones, cat.id, tipoTx,
          vista === 'mensual' ? anioActual : anioSel,
          vista === 'mensual' ? mesActual : null);
        const anterior = referenciaCat(cat.id);

        // Ritmo (solo mes actual): la referencia es el presupuesto de la
        // categoría si tiene; si no, lo gastado el mes anterior.
        const mesPasado         = sumCat(transacciones, cat.id, tipoTx, yAntR, mAntR);
        const limiteCat         = tipoTx === 'gasto'
          ? limiteMensualCategoria(presupuestos, cat.id, hoy.getFullYear(), hoy.getMonth() + 1) : null;
        const referencia        = limiteCat ?? mesPasado;
        const esPresupuesto     = limiteCat !== null;
        const ritmoEsperado     = referencia > 0 && totalDiasMesActual > 0
          ? (referencia / totalDiasMesActual) * diaHoy : 0;
        const diferenciaCat     = actual - ritmoEsperado;          // + = gasta más; - = menos
        const disponible        = referencia - actual;              // margen hasta la referencia
        const costoDiarioActual = diaHoy > 0   ? actual   / diaHoy          : 0;
        const costoDiarioAnt    = totalDiasMesAnt > 0 ? mesPasado / totalDiasMesAnt : 0;
        // Proyección a fin de mes al ritmo actual
        const proyeccionMes     = costoDiarioActual * totalDiasMesActual;
        // Barra: actual vs referencia (= 100 %)
        const pctVsAnt  = referencia > 0
          ? Math.min((actual / referencia) * 100, 120) : (actual > 0 ? 100 : 0);
        // Marcador de ritmo: % del mes transcurrido
        const pctRitmo  = totalDiasMesActual > 0
          ? Math.min((diaHoy / totalDiasMesActual) * 100, 100) : 0;

        return {
          cat, actual, anterior, diff: actual - anterior,
          // Media mensual (vista anual): el año en curso, entre los meses que llevamos
          equivMensual:    actual   / mesesMedia,
          equivMensualAnt: anterior / 12,
          ritmo: {
            referencia, esPresupuesto,
            ritmoEsperado, diferencia: diferenciaCat,
            disponible, diasRestantes,
            costoDiarioActual, costoDiarioAnt,
            proyeccionMes,
            pctVsAnt, pctRitmo,
            diaHoy, totalDiasMesActual, totalDiasMesAnt,
          },
        };
      })
      .filter(d => d.actual > 0 || d.anterior > 0)
      .sort((a, b) => b.actual - a.actual);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transacciones, categorias, presupuestos, tipoTx, vista, anioActual, mesActual, anioSel, referenciaCat, mesesMedia]);

  const totalActual      = datos.reduce((s, d) => s + d.actual, 0);
  const totalAnterior    = datos.reduce((s, d) => s + d.anterior, 0);
  const totalDiff        = totalActual - totalAnterior;
  const totalEquivMes    = totalActual   / mesesMedia;
  const totalEquivMesAnt = totalAnterior / 12;
  const maxValor         = Math.max(...datos.map(d => Math.max(d.actual, d.anterior)), 1);

  // ── Transacciones para el presupuesto/sin-presupuesto expandido ─────────────
  const txExpandida = useMemo(() => {
    if (!expandedBudgetKey) return [];
    const sepIdx = expandedBudgetKey.indexOf('::');
    if (sepIdx === -1) return [];
    const catId      = expandedBudgetKey.slice(0, sepIdx);
    const budgetPart = expandedBudgetKey.slice(sepIdx + 2); // id de presupuesto, o 'sin'
    const isSin      = budgetPart === 'sin';

    // Ids de presupuesto activos en el periodo mostrado para esta categoría
    // (incluye mensuales y anuales, según el periodo de la vista).
    const anioRef = vista === 'mensual' ? anioActual : anioSel;
    const mesRef  = vista === 'mensual' ? mesActual : null;
    const idsPeriodo = new Set(
      presupuestos
        .filter(p => p.categoriaId === catId && presupuestoEnPeriodo(p, anioRef, mesRef))
        .map(p => p.id),
    );

    return transacciones
      .filter(t => {
        if (t.tipo !== tipoTx || !computa(t)) return false;
        if (t.categoriaId !== catId) return false;
        const f = new Date(t.fecha);
        if (f.getFullYear() !== (vista === 'mensual' ? anioActual : anioSel)) return false;
        if (vista === 'mensual' && f.getMonth() + 1 !== mesActual) return false;
        // Huérfana: sin presupuesto o cuyo presupuesto no existe en el periodo.
        if (isSin) return !t.presupuestoId || !idsPeriodo.has(t.presupuestoId);
        return t.presupuestoId === budgetPart;
      })
      .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  }, [expandedBudgetKey, transacciones, tipoTx, vista, anioActual, mesActual, anioSel, presupuestos]);

  // ── Helpers visuales ─────────────────────────────────────────────────────────
  const colorDiff = (diff: number) => {
    if (Math.abs(diff) < 0.01) return 'text-gray-500 dark:text-gray-400';
    const mala = tipoTx === 'gasto' ? diff > 0 : diff < 0;
    return mala ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400';
  };
  const iconDiff = (diff: number) => {
    if (Math.abs(diff) < 0.01) return <Minus className="w-3.5 h-3.5" />;
    return diff > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />;
  };

  const getNombreCuenta = (id: string) => cuentas.find(c => c.id === id)?.nombre ?? 'Desconocida';

  // ── PDF ──────────────────────────────────────────────────────────────────────
  // C5: con Plus, el informe completo del periodo; sin Plus, la tabla de categorías.
  const handleExportPDF = () => {
    if (esPlus) {
      exportarInformeCompletoPDF(datosFinanzas, {
        anio: vista === 'mensual' ? anioActual : anioSel,
        mes: vista === 'mensual' ? mesActual : null,
      }, periodoActualLabel);
      return;
    }
    exportarInformesPDF(
      datos.map(d => ({ nombre: d.cat.nombre, icono: d.cat.icono, actual: d.actual, anterior: d.anterior, diff: d.diff })),
      tipoTx, periodoActualLabel, periodoAntLabel,
      totalActual, totalAnterior, totalDiff,
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">

        {/* Cabecera */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Informes</h1>
          <button onClick={handleExportPDF}
            title={esPlus ? 'Informe completo del periodo' : 'Tabla de categorías (el informe completo es de Plus)'}
            className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold py-2 px-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition text-sm">
            <FileDown className="w-4 h-4" /> PDF
          </button>
        </div>

        {/* Secciones */}
        <div className="-mx-4 px-4 md:mx-0 md:px-0 mb-4 overflow-x-auto">
          <div className="flex gap-1.5 w-max md:w-auto md:flex-wrap" role="tablist">
            {SECCIONES.map(x => (
              <button key={x.id} type="button" role="tab" aria-selected={seccion === x.id} onClick={() => irSeccion(x.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition ${seccion === x.id
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                {x.nombre}
              </button>
            ))}
          </div>
        </div>

        {/* Controles */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 mb-6">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex gap-2">
              {(['mensual', 'anual'] as Vista[]).map(v => (
                <button key={v} onClick={() => setVista(v)}
                  className={`px-3 py-2 rounded-lg font-semibold text-sm transition ${vista === v ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                  {v === 'mensual' ? 'Mensual' : 'Anual'}
                </button>
              ))}
            </div>
            {seccion === 'categorias' && <div className="flex gap-2">
              <button onClick={() => setTipoTx('gasto')}
                className={`px-3 py-2 rounded-lg font-semibold text-sm transition ${tipoTx === 'gasto' ? 'bg-red-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                Gastos
              </button>
              <button onClick={() => setTipoTx('ingreso')}
                className={`px-3 py-2 rounded-lg font-semibold text-sm transition ${tipoTx === 'ingreso' ? 'bg-green-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                Ingresos
              </button>
            </div>}
            <div className="flex items-center gap-1 ml-auto">
              {vista === 'mensual' ? (
                <>
                  <button onClick={irMesAnterior} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition"><ChevronLeft className="w-5 h-5" /></button>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white min-w-[7rem] text-center capitalize">{periodoActualLabel}</span>
                  <button onClick={irMesSiguiente} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition"><ChevronRight className="w-5 h-5" /></button>
                </>
              ) : (
                <>
                  <button onClick={irAnioAnterior} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition"><ChevronLeft className="w-5 h-5" /></button>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white min-w-[3rem] text-center">{anioSel}</span>
                  <button onClick={() => setAnioSel(y => y + 1)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition"><ChevronRight className="w-5 h-5" /></button>
                </>
              )}
            </div>
          </div>
          {seccion === 'categorias' && vista === 'mensual' && (
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              <span className="text-xs text-gray-500 dark:text-gray-400">Comparar con</span>
              {([
                ['mesAnterior', 'Mes anterior'],
                ['anioPasado', 'Mismo mes del año pasado'],
                ['media6', 'Media de 6 meses'],
              ] as [Comparador, string][]).map(([id, texto]) => (
                <button key={id} type="button" onClick={() => elegirComparador(id)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition ${comparador === id ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                  {texto}{id !== 'mesAnterior' && !esPlus && <EtiquetaPlus />}
                </button>
              ))}
            </div>
          )}
        </div>

        {seccion === 'resumen' && <SeccionResumen vista={vistaInforme} />}
        {seccion === 'presupuestos' && <SeccionPresupuestos vista={vistaInforme} />}
        {seccion === 'cuentas' && <SeccionCuentas vista={vistaInforme} />}
        {seccion === 'habitos' && <SeccionHabitos vista={vistaInforme} />}
        {seccion === 'adelantos' && <SeccionAdelantos vista={vistaInforme} />}

        {seccion === 'categorias' && <>
        {/* Progreso mensual */}
        {progreso && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="font-bold text-gray-900 dark:text-white text-base">
                Progreso del mes — día {progreso.diaHoy} de {progreso.totalDiasMes}
              </h2>
              <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full font-semibold">
                Ref: {periodoAntLabel}
              </span>
            </div>
            {progreso.gastoPrevMes > 0 ? (
              <>
                <div className="mb-3">
                  <div className="flex justify-between text-sm mb-1.5 flex-wrap gap-1">
                    <span className="text-gray-600 dark:text-gray-400">
                      <span className="font-bold text-gray-900 dark:text-white">{formatoEuros(progreso.gastoActual)}</span>
                      {' '}de {formatoEuros(progreso.gastoPrevMes)} del mes anterior
                    </span>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      {((progreso.gastoActual / progreso.gastoPrevMes) * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div className={`h-3 rounded-full transition-all ${progreso.gastoActual > progreso.gastoPrevMes ? 'bg-red-500' : 'bg-blue-500'}`}
                      style={{ width: `${progreso.porcentajeBarra}%` }} />
                    <div className="absolute top-0 bottom-0 w-0.5 bg-gray-500 dark:bg-gray-300 rounded"
                      style={{ left: `${progreso.porcentajeRitmo}%` }}
                      title={`Ritmo esperado: ${formatoEuros(progreso.ritmoEsperado)}`} />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>Día {progreso.diaHoy} de {progreso.totalDiasMes}</span>
                    <span>Esperado hoy: {formatoEuros(progreso.ritmoEsperado)}</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[
                    { label: 'Esperado hoy', value: `${formatoEuros(progreso.ritmoEsperado)}`, cls: 'text-gray-900 dark:text-white', bg: 'bg-gray-50 dark:bg-gray-700/50' },
                    {
                      label: progreso.enRitmo ? 'En ritmo' : progreso.adelantado ? 'Por encima' : 'Por debajo',
                      value: progreso.enRitmo ? formatoEuros(0) : formatoEuros(progreso.diferencia, true),
                      cls: progreso.enRitmo ? 'text-gray-600 dark:text-gray-400' : progreso.adelantado ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400',
                      bg: progreso.enRitmo ? 'bg-gray-50 dark:bg-gray-700/50' : progreso.adelantado ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20',
                    },
                    { label: 'Mes anterior', value: `${formatoEuros(progreso.gastoPrevMes)}`, cls: 'text-gray-500 dark:text-gray-400', bg: 'bg-gray-50 dark:bg-gray-700/50' },
                  ].map(({ label, value, cls, bg }) => (
                    <div key={label} className={`${bg} rounded-lg p-2`}>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                      <p className={`font-bold text-sm mt-0.5 ${cls}`}>{value}</p>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">Sin datos del mes anterior para comparar.</p>
            )}
          </div>
        )}

        {/* Totales comparativos */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { lbl: periodoActualLabel, valor: totalActual, color: tipoTx === 'gasto' ? 'text-red-600' : 'text-green-600', bg: tipoTx === 'gasto' ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20' },
            { lbl: periodoAntLabel,    valor: totalAnterior, color: 'text-gray-700 dark:text-gray-300', bg: 'bg-white dark:bg-gray-800' },
            { lbl: 'Diferencia',       valor: totalDiff,    color: colorDiff(totalDiff), bg: 'bg-white dark:bg-gray-800' },
          ].map(({ lbl, valor, color, bg }) => (
            <div key={lbl} className={`${bg} rounded-xl shadow p-3 text-center`}>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide truncate capitalize leading-tight">{lbl}</p>
              <p className={`text-lg font-bold mt-1 ${color}`}>
                {formatoEuros(valor, lbl === 'Diferencia')}
              </p>
            </div>
          ))}
        </div>

        {/* Tabla / cards de categorías */}
        {datos.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-10 text-center text-gray-500 dark:text-gray-400">
            No hay datos de {tipoTx === 'gasto' ? 'gastos' : 'ingresos'} para este período.
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
            {/* Cabecera desktop */}
            <div className="hidden md:grid md:grid-cols-[1fr_auto_auto_auto_auto] gap-4 px-5 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              <span>Categoría</span>
              <span className="text-right w-24 capitalize">{periodoActualLabel.split(' ')[0]}</span>
              <span className="text-right w-24 capitalize">{periodoAntLabel.split(' ')[0]}</span>
              <span className="text-right w-24">Diferencia</span>
              <span className="w-6" />
            </div>

            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {datos.map(({ cat, actual, anterior, diff, ritmo, equivMensual, equivMensualAnt }) => {
                const pctActual   = (actual   / maxValor) * 100;
                const pctAnterior = (anterior / maxValor) * 100;
                const isExpanded  = expandedCatId === cat.id;

                return (
                  <div key={cat.id}>
                    {/* Fila de categoría — clickable para expandir */}
                    <button
                      type="button"
                      onClick={() => { setExpandedCatId(isExpanded ? null : cat.id); setExpandedBudgetKey(null); }}
                      className="w-full text-left px-4 py-4 md:px-5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition"
                    >
                      {/* Móvil */}
                      <div className="md:hidden">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-8 h-8 rounded-lg flex items-center justify-center text-base shrink-0" style={{ backgroundColor: cat.color + '22' }}>
                              {cat.icono}
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white text-sm truncate">{cat.nombre}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="text-right">
                              <p className={`text-sm font-bold ${tipoTx === 'gasto' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                {formatoEuros(actual)}
                              </p>
                              {vista === 'anual' && (
                                <p className="text-[10px] text-gray-400 dark:text-gray-500">~{formatoEuros(equivMensual)}/mes</p>
                              )}
                            </div>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 pl-10 mb-1.5">
                          <div>
                            <span>Anterior: {formatoEuros(anterior)}</span>
                            {vista === 'anual' && (
                              <span className="ml-1 text-[10px] text-gray-400">(~{formatoEuros(equivMensualAnt)}/mes)</span>
                            )}
                          </div>
                          <span className={`flex items-center gap-0.5 font-semibold ${colorDiff(diff)}`}>{iconDiff(diff)}{formatoEuros(diff, true)}</span>
                        </div>
                        <div className="pl-10 space-y-1">
                          <div className="bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
                            <div className={`h-1.5 rounded-full ${tipoTx === 'gasto' ? 'bg-red-400' : 'bg-green-400'}`} style={{ width: `${pctActual}%` }} />
                          </div>
                          <div className="bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
                            <div className="h-1.5 rounded-full bg-gray-400 dark:bg-gray-500" style={{ width: `${pctAnterior}%` }} />
                          </div>
                        </div>
                      </div>

                      {/* Desktop */}
                      <div className="hidden md:block">
                        <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 items-center mb-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-8 h-8 rounded-lg flex items-center justify-center text-base shrink-0" style={{ backgroundColor: cat.color + '22' }}>
                              {cat.icono}
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white text-sm truncate">{cat.nombre}</span>
                          </div>
                          <div className="w-24 text-right">
                            <p className={`text-sm font-bold ${tipoTx === 'gasto' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>{formatoEuros(actual)}</p>
                            {vista === 'anual' && <p className="text-[10px] text-gray-400 dark:text-gray-500">~{formatoEuros(equivMensual)}/mes</p>}
                          </div>
                          <div className="w-24 text-right">
                            <p className="text-sm text-gray-400 dark:text-gray-500">{formatoEuros(anterior)}</p>
                            {vista === 'anual' && <p className="text-[10px] text-gray-400 dark:text-gray-500">~{formatoEuros(equivMensualAnt)}/mes</p>}
                          </div>
                          <span className={`flex items-center justify-end gap-0.5 text-sm font-semibold w-24 ${colorDiff(diff)}`}>{iconDiff(diff)}{formatoEuros(diff, true)}</span>
                          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                        <div className="space-y-1 pl-10">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 w-16 shrink-0 truncate capitalize">{periodoActualLabel.split(' ')[0]}</span>
                            <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                              <div className={`h-2 rounded-full transition-all ${tipoTx === 'gasto' ? 'bg-red-500' : 'bg-green-500'}`} style={{ width: `${pctActual}%` }} />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 w-16 shrink-0 truncate capitalize">{periodoAntLabel.split(' ')[0]}</span>
                            <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                              <div className="h-2 rounded-full bg-gray-400 dark:bg-gray-500 transition-all" style={{ width: `${pctAnterior}%` }} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* ── Panel de ritmo por categoría (solo mes actual) ─── */}
                    {esMesActual && (ritmo.referencia > 0 || actual > 0) && (() => {
                      const r = ritmo;
                      const excedido  = actual > r.referencia && r.referencia > 0;
                      const sinRef    = r.referencia === 0;
                      const nombreRef = r.esPresupuesto ? 'presupuesto' : 'mes ant.';
                      const adelanta  = r.diferencia > 0.01;
                      const enRitmoCat = Math.abs(r.diferencia) <= 0.01;

                      return (
                        <div className="mx-4 md:mx-5 mb-3 bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
                          {sinRef ? (
                            <p className="text-xs text-gray-400 text-center py-1">
                              Sin presupuesto ni datos del mes anterior: no hay referencia para comparar.
                            </p>
                          ) : (
                            <>
                              {/* Barra: gastado vs mes anterior */}
                              <div className="mb-2.5">
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-gray-600 dark:text-gray-400">
                                    <span className="font-bold text-gray-900 dark:text-white">
                                      {formatoEuros(actual)}
                                    </span>
                                    {' '}de {formatoEuros(r.referencia)}{' '}
                                    <span className="text-gray-400">({nombreRef})</span>
                                  </span>
                                  <span className={`font-semibold ${excedido ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                    {Math.min(r.pctVsAnt, 999).toFixed(0)}%
                                  </span>
                                </div>
                                <div className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                                  <div
                                    className={`h-2.5 rounded-full transition-all ${excedido ? 'bg-red-500' : 'bg-blue-500'}`}
                                    style={{ width: `${Math.min(r.pctVsAnt, 100)}%` }}
                                  />
                                  {/* Marcador del día de hoy (% del mes) */}
                                  <div
                                    className="absolute top-0 bottom-0 w-0.5 bg-gray-500 dark:bg-gray-300 rounded"
                                    style={{ left: `${r.pctRitmo}%` }}
                                    title={`Ritmo esperado: ${formatoEuros(r.ritmoEsperado)}`}
                                  />
                                </div>
                                <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                                  <span>Día {r.diaHoy} de {r.totalDiasMesActual}</span>
                                  <span>Esperado hoy: {formatoEuros(r.ritmoEsperado)}</span>
                                </div>
                              </div>

                              {/* Cuadrícula de métricas */}
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">

                                {/* Esperado hoy */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-2 text-center">
                                  <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">Esperado hoy</p>
                                  <p className="text-xs font-bold text-gray-900 dark:text-white mt-0.5">
                                    {formatoEuros(r.ritmoEsperado)}
                                  </p>
                                </div>

                                {/* Diferencia respecto al ritmo */}
                                <div className={`rounded-lg p-2 text-center ${
                                  enRitmoCat ? 'bg-white dark:bg-gray-800'
                                  : adelanta  ? 'bg-red-50 dark:bg-red-900/20'
                                  : 'bg-green-50 dark:bg-green-900/20'
                                }`}>
                                  <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                                    {enRitmoCat ? 'En ritmo' : adelanta ? 'Por encima' : 'Por debajo'}
                                  </p>
                                  <p className={`text-xs font-bold mt-0.5 flex items-center justify-center gap-0.5 ${
                                    enRitmoCat ? 'text-gray-600 dark:text-gray-400'
                                    : adelanta  ? 'text-red-600 dark:text-red-400'
                                    : 'text-green-600 dark:text-green-400'
                                  }`}>
                                    {enRitmoCat ? formatoEuros(0) : formatoEuros(r.diferencia, true)}
                                  </p>
                                </div>

                                {/* Disponible hasta fin de mes (hasta la referencia) */}
                                <div className={`rounded-lg p-2 text-center ${
                                  r.disponible >= 0 ? 'bg-white dark:bg-gray-800' : 'bg-red-50 dark:bg-red-900/20'
                                }`}>
                                  <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                                    Disponible ({r.diasRestantes}d)
                                  </p>
                                  <p className={`text-xs font-bold mt-0.5 ${
                                    r.disponible >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'
                                  }`}>
                                    {formatoEuros(r.disponible)}
                                  </p>
                                </div>

                                {/* Gasto al día: este mes y el anterior */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-2 text-center">
                                  <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">Al día: este mes · anterior</p>
                                  <p className="text-xs font-bold text-gray-900 dark:text-white mt-0.5">
                                    {formatoEuros(r.costoDiarioActual)}
                                    <span className="text-gray-400 font-normal"> · </span>
                                    {formatoEuros(r.costoDiarioAnt)}
                                  </p>
                                </div>
                              </div>

                              {/* Proyección fin de mes */}
                              {r.diasRestantes > 0 && r.costoDiarioActual > 0 && (
                                <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center mt-2">
                                  Al ritmo actual (~{formatoEuros(r.costoDiarioActual)}/día) terminarás el mes en{' '}
                                  <span className={`font-semibold ${r.proyeccionMes > r.referencia ? 'text-red-500' : 'text-gray-600 dark:text-gray-300'}`}>
                                    ~{formatoEuros(r.proyeccionMes)}
                                  </span>
                                  {r.referencia > 0 && (
                                    <span className="ml-1">
                                      ({formatoEuros(r.proyeccionMes - r.referencia, true)} vs {nombreRef})
                                    </span>
                                  )}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })()}

                    {isExpanded && (
                      <EvolucionCategoria categoriaId={cat.id} tipo={tipoTx}
                        anioFin={vista === 'mensual' ? anioActual : anioSel}
                        mesFin={vista === 'mensual' ? mesActual : anioSel === hoy.getFullYear() ? hoy.getMonth() + 1 : 12} />
                    )}

                    {/* ── Desglose por presupuesto ─────────────────────── */}
                    {isExpanded && (() => {
                      const anioRef = vista === 'mensual' ? anioActual : anioSel;
                      const mesRef  = vista === 'mensual' ? mesActual : null;
                      // Modelo evergreen: un único documento por presupuesto, sin
                      // deduplicación por grupo. Presupuestos de esta categoría que
                      // aplican al periodo (mensuales y anuales).
                      const presupuestosCat = presupuestos.filter(p =>
                        p.categoriaId === cat.id && presupuestoEnPeriodo(p, anioRef, mesRef)
                      );
                      const idsPeriodo = new Set(presupuestosCat.map(p => p.id));

                      // Transacciones de la categoría en el periodo.
                      const txsCat = transacciones.filter(t => {
                        if (t.tipo !== tipoTx || !computa(t)) return false;
                        if (t.categoriaId !== cat.id) return false;
                        const f = new Date(t.fecha);
                        if (f.getFullYear() !== (vista === 'mensual' ? anioActual : anioSel)) return false;
                        if (vista === 'mensual' && f.getMonth() + 1 !== mesActual) return false;
                        return true;
                      });

                      // Nº de meses en que un presupuesto MENSUAL aplica dentro del
                      // año mostrado (para escalar su límite en vista anual): desde su
                      // mes de inicio hasta diciembre; los puntuales solo cuentan 1.
                      const mesesActivosEnAnio = (p: typeof presupuestosCat[0]) => {
                        if (p.periodicidad !== 'mensual' || !p.repetitivo) return 1;
                        const [iy, im] = p.mes.split('-').map(Number);
                        if (iy < anioRef) return 12;
                        if (iy === anioRef) return 13 - im;
                        return 0;
                      };

                      // Imputación por id de presupuesto (evergreen).
                      const gastoPorPres = presupuestosCat.map(p => {
                        // Vista mensual: un anual se prorratea /12 para comparar con el
                        // gasto del mes. Vista anual: un mensual se escala por sus meses
                        // activos para dar el límite anual comparable.
                        // Los mensuales usan el importe de cada mes (puede cambiar de un mes a otro).
                        const limiteDelMes = p.periodicidad === 'mensual' && vista === 'mensual'
                          ? limiteEnPeriodo(p, `${anioRef}-${String(mesRef).padStart(2, '0')}`)
                          : p.limite;
                        const limiteProrrateado =
                          vista === 'mensual' && p.periodicidad === 'anual' ? p.limite / 12
                          : vista === 'anual' && p.periodicidad === 'mensual'
                            ? (p.repetitivo ? limiteMensualEnAnio(p, anioRef) : p.limite * mesesActivosEnAnio(p))
                          : limiteDelMes;
                        return {
                          p,
                          limiteProrrateado,
                          // Solo el anual partido en meses lleva la nota «anual ÷ 12»
                          prorrateado: vista === 'mensual' && p.periodicidad === 'anual',
                          gasto: txsCat
                            .filter(t => t.presupuestoId === p.id)
                            .reduce((s, t) => s + t.cantidad, 0),
                        };
                      });

                      // Sin presupuesto = sin presupuestoId o referencia huérfana
                      // (presupuesto inexistente en el periodo mostrado).
                      const gastoSinPres = txsCat
                        .filter(t => !t.presupuestoId || !idsPeriodo.has(t.presupuestoId))
                        .reduce((s, t) => s + t.cantidad, 0);

                      const sinKey = `${cat.id}::sin`;

                      const renderTxs = (txs: typeof txExpandida) =>
                        txs.length === 0 ? (
                          <p className="text-xs text-gray-400 text-center py-2">Sin transacciones.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {txs.map(tx => (
                              <div key={tx.id} className="flex items-center justify-between gap-3 bg-white dark:bg-gray-800 rounded-lg px-3 py-2 shadow-sm">
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm text-gray-800 dark:text-gray-200 truncate">
                                    {tx.descripcion || <span className="text-gray-400 italic">Sin descripción</span>}
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    {format(new Date(tx.fecha), 'dd MMM yyyy', { locale: es })} · {getNombreCuenta(tx.cuentaId)}
                                  </p>
                                </div>
                                <span className={`text-sm font-bold shrink-0 ${tipoTx === 'gasto' ? 'text-red-600' : 'text-green-600'}`}>
                                  {tipoTx === 'gasto' ? '-' : '+'}{formatoEuros(tx.cantidad)}
                                </span>
                              </div>
                            ))}
                          </div>
                        );

                      return (
                        <div className="border-t border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">

                          {/* Filas de presupuesto */}
                          {gastoPorPres.map(({ p, gasto, limiteProrrateado, prorrateado }) => {
                            const key       = `${cat.id}::${p.id}`;
                            const isExpB    = expandedBudgetKey === key;
                            const pct       = limiteProrrateado > 0 ? Math.min((gasto / limiteProrrateado) * 100, 100) : 0;
                            const excedido  = limiteProrrateado > 0 && gasto > limiteProrrateado;
                            const esAnual   = p.periodicidad === 'anual';
                            return (
                              <div key={p.id}>
                                <button
                                  type="button"
                                  onClick={() => setExpandedBudgetKey(isExpB ? null : key)}
                                  className="w-full text-left px-4 md:px-5 py-3 pl-10 md:pl-12 hover:bg-gray-100 dark:hover:bg-gray-700/40 transition"
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="text-base shrink-0">📋</span>
                                    <span className="text-sm text-gray-700 dark:text-gray-200 truncate">{p.nombre}</span>
                                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${
                                      esAnual
                                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                    }`}>
                                      {esAnual ? 'Anual' : 'Mensual'}
                                    </span>
                                    <div className="text-right shrink-0 ml-auto">
                                      <span className={`text-sm font-semibold ${excedido ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-200'}`}>
                                        {formatoEuros(gasto)}
                                      </span>
                                      <span className="text-xs text-gray-400"> / {formatoEuros(limiteProrrateado)}</span>
                                      {prorrateado && (
                                        <span className="text-[10px] text-gray-400 block">anual {formatoEuros(p.limite)} ÷ 12</span>
                                      )}
                                    </div>
                                    <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${isExpB ? 'rotate-180' : ''}`} />
                                  </div>
                                  <div className="mt-1.5 pl-6">
                                    <div className="bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                                      <div className={`h-1.5 rounded-full transition-all ${excedido ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                                    </div>
                                    <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                                      <span>{pct.toFixed(0)}% del límite</span>
                                      <span className={excedido ? 'text-red-500' : ''}>
                                        {excedido
                                          ? `Excedido ${formatoEuros((gasto - limiteProrrateado))}`
                                          : `Restante ${formatoEuros((limiteProrrateado - gasto))}`}
                                      </span>
                                    </div>
                                  </div>
                                </button>
                                {isExpB && (
                                  <div className="bg-gray-50 dark:bg-gray-900/30 px-4 md:px-5 py-3 border-t border-gray-100 dark:border-gray-700">
                                    {renderTxs(txExpandida)}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Sin presupuesto */}
                          {gastoSinPres > 0 && (() => {
                            const isExpSin = expandedBudgetKey === sinKey;
                            return (
                              <div>
                                <button
                                  type="button"
                                  onClick={() => setExpandedBudgetKey(isExpSin ? null : sinKey)}
                                  className="w-full text-left px-4 md:px-5 py-3 pl-10 md:pl-12 bg-orange-50/70 dark:bg-orange-900/10 hover:bg-orange-100 dark:hover:bg-orange-900/20 transition"
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="text-base shrink-0">⚪</span>
                                    <span className="text-sm text-orange-700 dark:text-orange-300 font-medium flex-1">Sin presupuesto</span>
                                    <span className="text-sm font-semibold text-orange-600 dark:text-orange-400 shrink-0">{formatoEuros(gastoSinPres)}</span>
                                    <ChevronDown className={`w-3.5 h-3.5 text-orange-400 shrink-0 transition-transform ${isExpSin ? 'rotate-180' : ''}`} />
                                  </div>
                                </button>
                                {isExpSin && (
                                  <div className="bg-orange-50/30 dark:bg-orange-900/5 px-4 md:px-5 py-3 border-t border-orange-100 dark:border-orange-900/20">
                                    {renderTxs(txExpandida)}
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {/* Sin datos */}
                          {gastoPorPres.length === 0 && gastoSinPres === 0 && (
                            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">Sin transacciones en este período.</p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>

            {/* Pie totales */}
            <div className="px-4 md:px-5 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
              {/* Móvil */}
              <div className="flex items-center justify-between md:hidden">
                <span className="text-sm font-bold text-gray-900 dark:text-white">TOTAL</span>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className={`text-sm font-bold ${tipoTx === 'gasto' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>{formatoEuros(totalActual)}</p>
                    {vista === 'anual' && <p className="text-[10px] text-gray-400">~{formatoEuros(totalEquivMes)}/mes</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">vs {formatoEuros(totalAnterior)}</p>
                    {vista === 'anual' && <p className="text-[10px] text-gray-400">~{formatoEuros(totalEquivMesAnt)}/mes</p>}
                  </div>
                  <span className={`flex items-center gap-0.5 text-sm font-bold ${colorDiff(totalDiff)}`}>{iconDiff(totalDiff)}{formatoEuros(totalDiff, true)}</span>
                </div>
              </div>
              {/* Desktop */}
              <div className="hidden md:grid md:grid-cols-[1fr_auto_auto_auto_auto] gap-4 items-start">
                <span className="text-sm font-bold text-gray-900 dark:text-white self-center">TOTAL</span>
                <div className="w-24 text-right">
                  <p className={`text-sm font-bold ${tipoTx === 'gasto' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>{formatoEuros(totalActual)}</p>
                  {vista === 'anual' && <p className="text-[10px] text-gray-400 dark:text-gray-500">~{formatoEuros(totalEquivMes)}/mes</p>}
                </div>
                <div className="w-24 text-right">
                  <p className="text-sm font-semibold text-gray-400">{formatoEuros(totalAnterior)}</p>
                  {vista === 'anual' && <p className="text-[10px] text-gray-400 dark:text-gray-500">~{formatoEuros(totalEquivMesAnt)}/mes</p>}
                </div>
                <span className={`flex items-center justify-end gap-0.5 text-sm font-bold w-24 self-center ${colorDiff(totalDiff)}`}>{iconDiff(totalDiff)}{formatoEuros(totalDiff, true)}</span>
                <span className="w-6" />
              </div>
              {/* Nota explicativa en vista anual */}
              {vista === 'anual' && (
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2 text-center">
                  Media mensual: el total entre {mesesMedia === 12 ? '12 meses' : `los ${mesesMedia} meses que llevamos de año`}{mesesMedia !== 12 && ' (el año anterior, entre 12)'}
                </p>
              )}
            </div>
          </div>
        )}
        </>}
      </div>
    </div>
  );
};
